/**
 * EmailsPage — Full-page email list with search and detail view.
 *
 * Master-detail: email list on the left, email body on the right.
 * Fetches from coreBOS Emails module.
 */

import React, { useState } from "react";
import {
  Input,
  Typography,
  Spin,
  Empty,
  Tag,
  Divider,
  theme,
} from "antd";
import {
  SearchOutlined,
  MailOutlined,
  SendOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import { useList } from "@refinedev/core";
import { timeAgo } from "@/utils/timeAgo";

const { Text, Title } = Typography;

export const EmailsPage: React.FC = () => {
  const {
    token: { colorBgContainer, colorBorder, borderRadiusLG, colorPrimaryBg, fontSizeSM, colorBgTextHover },
  } = theme.useToken();

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [flagFilter, setFlagFilter] = useState<string | null>(null);

  const { data: emailsData, isLoading } = useList({
    resource: "emails",
    pagination: { pageSize: 100 },
    sorters: [{ field: "createdtime", order: "desc" }],
    queryOptions: { refetchInterval: 60000 },
  });

  const emails = (emailsData?.data || []) as any[];

  // Filter
  const filtered = emails.filter((e) => {
    if (flagFilter && e.email_flag !== flagFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        (e.subject || "").toLowerCase().includes(q) ||
        (e.from_email || "").toLowerCase().includes(q) ||
        (e.saved_toid || "").toLowerCase().includes(q)
      );
    }
    return true;
  });

  const selectedEmail = selectedId
    ? emails.find((e) => e.id === selectedId)
    : null;

  const parseRecipients = (json: string): string[] => {
    try {
      return JSON.parse(json || "[]")
        .map((s: string) => s.trim())
        .filter(Boolean);
    } catch {
      return [];
    }
  };

  return (
    <div
      style={{
        display: "flex",
        gap: 0,
        height: "calc(100vh - 96px)",
        background: colorBgContainer,
        borderRadius: borderRadiusLG,
        overflow: "hidden",
        border: `1px solid ${colorBorder}`,
      }}
    >
      {/* Left: Email list */}
      <div
        style={{
          width: 400,
          flexShrink: 0,
          borderRight: `1px solid ${colorBorder}`,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div style={{ padding: "16px 16px 12px" }}>
          <Title level={4} style={{ margin: 0, marginBottom: 12 }}>
            <MailOutlined style={{ marginRight: 8 }} />
            Email
          </Title>

          <Input
            placeholder="Cerca per oggetto, mittente, destinatario..."
            prefix={<SearchOutlined />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            allowClear
            size="small"
            style={{ marginBottom: 8 }}
          />

          <div style={{ display: "flex", gap: 4 }}>
            <Tag
              color={flagFilter === null ? "blue" : undefined}
              style={{ cursor: "pointer", margin: 0 }}
              onClick={() => setFlagFilter(null)}
            >
              Tutte
            </Tag>
            <Tag
              color={flagFilter === "SENT" ? "blue" : undefined}
              style={{ cursor: "pointer", margin: 0 }}
              onClick={() => setFlagFilter(flagFilter === "SENT" ? null : "SENT")}
              icon={<SendOutlined />}
            >
              Inviate
            </Tag>
            <Tag
              color={flagFilter === "RECEIVED" ? "blue" : undefined}
              style={{ cursor: "pointer", margin: 0 }}
              onClick={() => setFlagFilter(flagFilter === "RECEIVED" ? null : "RECEIVED")}
              icon={<InboxOutlined />}
            >
              Ricevute
            </Tag>
          </div>
        </div>

        <Divider style={{ margin: 0 }} />

        <div style={{ flex: 1, overflow: "auto" }}>
          {isLoading ? (
            <div style={{ textAlign: "center", padding: 40 }}>
              <Spin />
            </div>
          ) : filtered.length === 0 ? (
            <Empty
              description={searchQuery ? "Nessun risultato" : "Nessuna email"}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              style={{ margin: "40px 0" }}
            />
          ) : (
            filtered.map((email) => {
              const to = parseRecipients(email.saved_toid);
              const isSelected = email.id === selectedId;

              return (
                <div
                  key={email.id}
                  onClick={() => setSelectedId(email.id)}
                  style={{
                    padding: "10px 16px",
                    cursor: "pointer",
                    background: isSelected ? colorPrimaryBg : "transparent",
                    borderLeft: isSelected ? "3px solid #6366f1" : "3px solid transparent",
                    borderBottom: `1px solid ${colorBorder}`,
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected)
                      (e.currentTarget as HTMLElement).style.background = colorBgTextHover;
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected)
                      (e.currentTarget as HTMLElement).style.background = "transparent";
                  }}
                >
                  {/* Subject + time */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "baseline",
                      gap: 8,
                    }}
                  >
                    <Text
                      strong
                      style={{
                        fontSize: 13,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        flex: 1,
                      }}
                    >
                      {email.subject || "(nessun oggetto)"}
                    </Text>
                    <Text
                      type="secondary"
                      style={{ fontSize: fontSizeSM - 1, flexShrink: 0 }}
                    >
                      {timeAgo(email.createdtime)}
                    </Text>
                  </div>

                  {/* From / To */}
                  <Text
                    type="secondary"
                    style={{
                      fontSize: 11,
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {email.email_flag === "SENT" ? (
                      <>
                        <SendOutlined style={{ marginRight: 4 }} />→{" "}
                        {to.join(", ") || "—"}
                      </>
                    ) : (
                      <>
                        <InboxOutlined style={{ marginRight: 4 }} />
                        {email.from_email || "—"}
                      </>
                    )}
                  </Text>

                  {/* Tracking badges */}
                  {(email.delivered === "1" || email.open === "1" || email.bounce === "1") && (
                    <div style={{ marginTop: 4, display: "flex", gap: 4 }}>
                      {email.delivered === "1" && (
                        <Tag color="green" style={{ fontSize: 10, lineHeight: "16px", margin: 0 }}>
                          Consegnata
                        </Tag>
                      )}
                      {email.open === "1" && (
                        <Tag color="blue" style={{ fontSize: 10, lineHeight: "16px", margin: 0 }}>
                          Aperta
                        </Tag>
                      )}
                      {email.bounce === "1" && (
                        <Tag color="red" style={{ fontSize: 10, lineHeight: "16px", margin: 0 }}>
                          Bounce
                        </Tag>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Right: Email detail */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {selectedEmail ? (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            {/* Email header */}
            <div
              style={{
                padding: "16px 20px",
                borderBottom: `1px solid ${colorBorder}`,
              }}
            >
              <Title level={5} style={{ margin: 0, marginBottom: 8 }}>
                {selectedEmail.subject || "(nessun oggetto)"}
              </Title>

              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <strong>Da:</strong> {selectedEmail.from_email || "—"}
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <strong>A:</strong>{" "}
                  {parseRecipients(selectedEmail.saved_toid).join(", ") || "—"}
                </Text>
                {parseRecipients(selectedEmail.ccmail).length > 0 && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    <strong>CC:</strong>{" "}
                    {parseRecipients(selectedEmail.ccmail).join(", ")}
                  </Text>
                )}
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {selectedEmail.createdtime
                    ? new Date(
                        selectedEmail.createdtime.replace(" ", "T") + "Z"
                      ).toLocaleString("it-IT")
                    : ""}
                </Text>
              </div>
            </div>

            {/* Email body */}
            <div
              style={{
                flex: 1,
                overflow: "auto",
                padding: 20,
              }}
            >
              {selectedEmail.description ? (
                <div
                  style={{ fontSize: 14, lineHeight: 1.6 }}
                  dangerouslySetInnerHTML={{
                    __html: selectedEmail.description,
                  }}
                />
              ) : (
                <Empty description="Nessun contenuto" />
              )}
            </div>
          </div>
        ) : (
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Empty
              description="Seleziona una email"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </div>
        )}
      </div>
    </div>
  );
};
